import isNil from 'lodash/isNil'

export const SIDE_PANEL_REM = 16
export const SIDE_PANEL_DEFAULT_WIDTH = 28 * SIDE_PANEL_REM
export const SIDE_PANEL_DEFAULT_MIN_WIDTH = 20 * SIDE_PANEL_REM
export const SIDE_PANEL_DEFAULT_MAX_WIDTH = 56 * SIDE_PANEL_REM
export const SIDE_PANEL_DEFAULT_MIN_MAIN_CONTENT_WIDTH = 28 * SIDE_PANEL_REM
export const SIDE_PANEL_WIDTH_STORAGE_KEY = 'designSystem.SidePanel.width'

export function resolveSidePanelMaxWidth(
  maxWidthProp: number | undefined,
  openWidthRatio: number | undefined
): number | undefined {
  if (!isNil(maxWidthProp)) return maxWidthProp
  if (isNil(openWidthRatio)) return SIDE_PANEL_DEFAULT_MAX_WIDTH
  return undefined
}

export function clampSidePanelWidth({
  maxWidth,
  minWidth,
  width,
}: {
  maxWidth: number | undefined
  minWidth: number
  width: number
}): number {
  const atLeastMin = Math.max(minWidth, width)
  return isNil(maxWidth) ? atLeastMin : Math.min(maxWidth, atLeastMin)
}

export function resolveKeyboardPanelMaxWidth({
  containerWidth,
  maxWidth,
}: {
  containerWidth: number | undefined
  maxWidth: number | undefined
}): number {
  if (isNil(containerWidth) || !Number.isFinite(containerWidth)) {
    return isNil(maxWidth)
      ? SIDE_PANEL_DEFAULT_MAX_WIDTH
      : Math.min(maxWidth, SIDE_PANEL_DEFAULT_MAX_WIDTH)
  }
  return isNil(maxWidth) ? containerWidth : Math.min(maxWidth, containerWidth)
}

export function clampOpenWidthRatio(
  openWidthRatio: number | undefined
): number | undefined {
  if (isNil(openWidthRatio)) return undefined
  return Math.min(1, Math.max(0, openWidthRatio))
}

export function preferredPanelTrackForMode({
  ratio,
  usesOpenWidthRatio,
}: {
  ratio: number | undefined
  usesOpenWidthRatio: boolean
}): string {
  return usesOpenWidthRatio && !isNil(ratio)
    ? `${ratio * 100}%`
    : 'var(--sp-w, 28rem)'
}

export function pixelWidthForOpenRatio({
  containerWidth,
  maxWidth,
  minWidth,
  openWidthRatio,
}: {
  containerWidth: number
  maxWidth: number | undefined
  minWidth: number
  openWidthRatio: number
}): number {
  const ratio = Math.min(1, Math.max(0, openWidthRatio))
  return clampSidePanelWidth({
    maxWidth,
    minWidth,
    width: containerWidth * ratio,
  })
}

export function readSidePanelStoredWidth(
  fallback: number,
  storageKey: string = SIDE_PANEL_WIDTH_STORAGE_KEY
): number {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === null) return fallback
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

export function hasSidePanelStoredWidth(storageKey: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.localStorage.getItem(storageKey)
    return raw !== null && Number.isFinite(Number(raw))
  } catch {
    return false
  }
}

export function writeSidePanelStoredWidth(
  value: number,
  storageKey: string = SIDE_PANEL_WIDTH_STORAGE_KEY
) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, String(value))
  } catch {
    /* noop */
  }
}

/** Unbounded ratio panels must not write the shared SidePanel width key. */
export function shouldPersistSidePanelStoredWidth({
  maxWidthProp,
  openWidthRatio,
}: {
  maxWidthProp: number | undefined
  openWidthRatio: number | undefined
}): boolean {
  return isNil(openWidthRatio) || !isNil(maxWidthProp)
}
